import * as React from "react";

import { BookingWindow, RollingLinkRange, PublicRollingLinkSlot } from "@/api/types";
import { cn } from "@/lib/utils";

import { AvailabilityCalendar } from "../AvailabilityCalendar";
import { BlockedSlot, BookedSlot, BookingCalendar } from "../BookingCalendar";

/**
 * The rolling calendar's availability editor. Reuses the ad-hoc drag calendar
 * ({@link AvailabilityCalendar}) — one grid, one shared time axis, so the whole week
 * both weeks scroll together — but the availability drawn here is a REPEATING pattern (weekly, or a
 * two-week Week A / Week B), not dated. Windows drawn on the grid are converted to/from the rolling link's
 * {@code (weekday, HH:mm)} bands. Below it, a read-only preview shows what invitees would
 * actually be offered (the server has already removed the host's busy time).
 */

// --- date helpers (all local-time; the rolling link zone is the browser zone in the builder) ---

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}
function mondayOf(d: Date): Date {
  const out = startOfDay(d);
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7)); // getDay: 0=Sun → shift so Monday is start
  return out;
}
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function atTime(day: Date, hm: string): Date {
  const [h, m] = hm.split(":").map(Number);
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), h || 0, m || 0);
}

/** Project the repeating bands onto the concrete dates the calendar is showing. */
function windowsFromRanges(
  ranges: RollingLinkRange[],
  viewFrom: string,
  viewTo: string,
): BookingWindow[] {
  const from = startOfDay(new Date(viewFrom));
  const to = new Date(viewTo);
  const out: BookingWindow[] = [];
  for (let d = new Date(from); d < to; d = addDays(d, 1)) {
    const weekday = d.getDay();
    for (const r of ranges) {
      if (r.weekday !== weekday) continue;
      out.push({ start: atTime(d, r.earliest).toISOString(), end: atTime(d, r.latest).toISOString() });
    }
  }
  return out;
}

/** Read the drawn dated windows back into repeating bands (dedupes identical bands). */
function rangesFromWindows(windows: BookingWindow[]): RollingLinkRange[] {
  const seen = new Set<string>();
  const out: RollingLinkRange[] = [];
  for (const w of windows) {
    const s = new Date(w.start);
    const weekday = s.getDay();
    const earliest = hhmm(w.start);
    const latest = hhmm(w.end);
    const key = `${weekday}-${earliest}-${latest}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ weekday, earliest, latest });
  }
  return out;
}

export function RollingAvailabilityCalendar({
  ranges,
  onRangesChange,
  slotMinutes,
  cursorMinutes,
  intervalMinutes,
  previewSlots,
  previewLoading,
  bookedSlots,
  blockedSlots,
  configSummary,
  fillControl,
}: {
  ranges: RollingLinkRange[];
  onRangesChange: (r: RollingLinkRange[]) => void;
  /** Appointment (fill) length — used for the invitee preview below the grid. */
  slotMinutes: number;
  /** Draw-cursor size — the block a plain click / hover cursor places on the grid. */
  cursorMinutes: number;
  /** Stride between slot starts (duration + gap) — makes the drawn slots show interval gaps. */
  intervalMinutes: number;
  previewSlots: PublicRollingLinkSlot[];
  previewLoading: boolean;
  /** Existing confirmed bookings, shown on the calendar as taken (host edit view only). */
  bookedSlots?: BookedSlot[];
  /** Slots the preview dropped for clashing with a meeting, shown as disabled conflict markers. */
  blockedSlots?: BlockedSlot[];
  /** Read-only settings summary shown centred in the calendar toolbar (like the ad-hoc builder). */
  configSummary?: React.ReactNode;
  /** The "Fill from a pattern" toolbar control, shown left beside the "Starts" date. */
  fillControl?: React.ReactNode;
}) {
  const booked = bookedSlots ?? [];
  const blocked = blockedSlots ?? [];
  // When there are bookings, let the host filter the calendar: open availability, taken slots, or both.
  const [bookingFilter, setBookingFilter] = React.useState<"all" | "open" | "booked">("all");
  const shownOpen = bookingFilter === "booked" ? [] : previewSlots;
  const shownBooked = bookingFilter === "open" ? [] : booked;
  // Blocked (meeting-clash) markers ride with the open set — they explain a dropped availability.
  const shownBlocked = bookingFilter === "booked" ? [] : blocked;

  // Anchored to THIS week's Monday: the repeating grid is shown for the whole current week
  // (Mon-Sun), including days earlier this week, drawn unshaded.
  const initialDate = React.useMemo(() => ymd(mondayOf(new Date())), []);

  const [viewRange, setViewRange] = React.useState(() => {
    const m = mondayOf(new Date());
    return { from: m.toISOString(), to: addDays(m, 7).toISOString() };
  });

  const windows = React.useMemo(
    () => windowsFromRanges(ranges, viewRange.from, viewRange.to),
    [ranges, viewRange],
  );


  return (
    <section className="space-y-3">
      {/* One grid, one time axis. The controls live in the calendar's own toolbar (Fill and Starts
          on the left, the config summary centre) so the top of the calendar uses the space well —
          no separate heading or helper row. The Weekly/Biweekly toggle went with the two-week
          cadence: a rolling link is one weekly grid now. */}
      <AvailabilityCalendar
        windows={windows}
        onChange={(next) => onRangesChange(rangesFromWindows(next))}
        defaultFrom="09:00"
        defaultTo="17:00"
        showMine={false}
        slotMinutes={cursorMinutes}
        intervalMinutes={intervalMinutes}
        patternMode
        initialDate={initialDate}
        onViewChange={(scope) => setViewRange({ from: scope.from, to: scope.to })}
        headerLeft={
          // Fill pill only, in the same toolbar position as the ad-hoc builder. The "Starts" date
          // went with V98: a link takes bookings the moment it exists, so there is no second,
          // invisible switch between publishing it and it working.
          <div className="flex flex-wrap items-center gap-2">{fillControl}</div>
        }
        headerCenter={configSummary}
      />

      {/* Live preview — what invitees get (busy already removed) — plus existing bookings when editing. */}
      <div className="space-y-2 rounded-xl border border-border bg-background/60 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-xs font-bold uppercase tracking-wide text-foreground">
            {booked.length > 0 ? "Preview & bookings" : "Preview"}
          </h4>
          <div className="flex items-center gap-2">
            {previewLoading && <span className="text-xs text-muted-foreground">updating…</span>}
            {booked.length > 0 && (
              <div className="inline-flex overflow-hidden rounded-lg border border-[hsl(var(--foreground)_/_0.28)] text-xs font-semibold">
                {(["all", "open", "booked"] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    aria-pressed={bookingFilter === f}
                    onClick={() => setBookingFilter(f)}
                    className={cn(
                      "px-2.5 py-1 capitalize transition",
                      bookingFilter === f
                        ? "bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {f === "booked" ? "Confirmed" : f}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {booked.length > 0 && (
          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-teal-500" /> Open to book
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-amber-500" /> Booked
            </span>
          </div>
        )}
        {shownOpen.length === 0 && shownBooked.length === 0 && shownBlocked.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {booked.length > 0
              ? "Nothing to show for this filter."
              : "No open times yet. Draw some hours above (slots that clash with a meeting or a taken booking won't show)."}
          </p>
        ) : (
          <>
            <BookingCalendar
              slots={shownOpen}
              booked={shownBooked}
              blockedSlots={shownBlocked}
              slotMinutes={slotMinutes}
              compact
            />
          </>
        )}
      </div>
    </section>
  );
}
